import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Calendar, Clock, AlertTriangle, CheckCircle, RotateCcw, Save, History, Trash2, PenTool, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SignaturePad, { SignaturePreview } from "../components/SignaturePad";
import { RegulatoryParametersCard } from "../components/RegulatoryParametersCard";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemSettingsPage() {
  const { user, token, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resettingDb, setResettingDb] = useState(false);
  
  // Current deadline state
  const [currentDeadline, setCurrentDeadline] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    annee: new Date().getFullYear() - 1,
    date_limite: "",
    motif: ""
  });
  
  // History state
  const [deadlineHistory, setDeadlineHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Confirmation state for database reset
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  
  // Signature state
  const [signatureData, setSignatureData] = useState(null);
  const [signatureInfo, setSignatureInfo] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);

  useEffect(() => {
    fetchCurrentDeadline();
    if (isSuperAdmin) {
      fetchSignature();
    }
  }, []);

  const fetchCurrentDeadline = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/settings/deadline/current`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentDeadline(data);
        
        // Pre-fill form with current year's deadline
        setFormData(prev => ({
          ...prev,
          annee: data.annee_bilan,
          date_limite: data.date_limite
        }));
      }
    } catch (error) {
      console.error(/* PROD */ "Error fetching deadline:", error);
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  // Signature functions
  const fetchSignature = async () => {
    setSignatureLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/signature`);
      setSignatureInfo(response.data);
      if (response.data.has_signature) {
        setSignatureData(response.data.signature_data);
      }
    } catch (error) {
      console.error(/* PROD */ "Error fetching signature:", error);
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSaveSignature = async (signatureBase64) => {
    try {
      await axios.post(`${API_URL}/api/settings/signature`, {
        signature_data: signatureBase64,
        signatory_title: "Inspecteur Général du Travail"
      });
      
      setSignatureData(signatureBase64);
      setSignatureInfo(prev => ({
        ...prev,
        has_signature: true,
        signature_data: signatureBase64,
        signatory_name: `${user?.prenom || ''} ${user?.nom || ''}`.trim(),
        updated_at: new Date().toISOString()
      }));
      
      toast.success("Signature électronique enregistrée avec succès");
    } catch (error) {
      console.error(/* PROD */ "Error saving signature:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
      throw error;
    }
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer votre signature électronique ?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/settings/signature`);
      setSignatureData(null);
      setSignatureInfo(prev => ({
        ...prev,
        has_signature: false,
        signature_data: null
      }));
      toast.success("Signature supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/deadlines`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeadlineHistory(data);
        setShowHistory(true);
      }
    } catch (error) {
      console.error(/* PROD */ "Error fetching history:", error);
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date_limite) {
      toast.error("Veuillez sélectionner une date");
      return;
    }
    
    if (!formData.motif || formData.motif.length < 10) {
      toast.error("Le motif doit contenir au moins 10 caractères");
      return;
    }
    
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/settings/deadline`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchCurrentDeadline();
        setFormData(prev => ({ ...prev, motif: "" }));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error(/* PROD */ "Error updating deadline:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser la date limite au 31 mars ?")) {
      return;
    }
    
    try {
      setResetting(true);
      const response = await fetch(`${API_URL}/api/settings/deadline/${formData.annee}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchCurrentDeadline();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la réinitialisation");
      }
    } catch (error) {
      console.error(/* PROD */ "Error resetting deadline:", error);
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres Système</h1>
          <p className="text-gray-600">Gérez les paramètres de la plateforme</p>
        </div>
      </div>

      {isSuperAdmin && <RegulatoryParametersCard />}

      {/* Signature Section - Super Admin Only */}
      {isSuperAdmin && (
        <Card data-testid="signature-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-[#1B4D3E]" />
              Signature Électronique Officielle
            </CardTitle>
            <CardDescription>
              Cette signature sera apposée sur tous les documents officiels (quittances, décisions d'habilitation).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {signatureLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#1B4D3E]" />
              </div>
            ) : (
              <>
                {/* Signature Preview */}
                <SignaturePreview
                  signature={signatureData}
                  onEdit={() => setShowSignaturePad(true)}
                />

                {/* Signature Info */}
                {signatureInfo?.has_signature && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-emerald-800">Signature configurée</p>
                        <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Signataire</p>
                            <p className="font-medium">{signatureInfo.signatory_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Titre</p>
                            <p className="font-medium">{signatureInfo.signatory_title}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setShowSignaturePad(true)}>
                        <PenTool className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={handleDeleteSignature}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                )}

                {!signatureInfo?.has_signature && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Aucune signature configurée</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Les documents officiels seront générés avec une signature par défaut.
                        </p>
                        <Button 
                          size="sm" 
                          className="mt-3 bg-[#1B4D3E] hover:bg-[#163d32]"
                          onClick={() => setShowSignaturePad(true)}
                        >
                          <PenTool className="w-4 h-4 mr-2" />
                          Créer ma signature
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents concernés */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <p className="text-sm font-medium text-gray-700 mb-3">Documents signés automatiquement :</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-[#1B4D3E]" />
                      <span>Quittances de bilans validés</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-[#1B4D3E]" />
                      <span>Décisions d'habilitation</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Deadline Status */}
      <Card data-testid="current-deadline-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <CardTitle>Date Limite de Soumission</CardTitle>
            </div>
            {currentDeadline?.is_extended && (
              <Badge variant="warning" className="bg-amber-100 text-amber-800">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Prolongée
              </Badge>
            )}
          </div>
          <CardDescription>
            Date limite pour la soumission des bilans sociaux {currentDeadline?.annee_bilan}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Current Date */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Date limite actuelle</div>
              <div className="text-2xl font-bold text-primary">
                {currentDeadline?.date_limite_formatted}
              </div>
              {currentDeadline?.is_extended && (
                <div className="text-xs text-gray-500 mt-1">
                  (Originale: {currentDeadline?.original_date})
                </div>
              )}
            </div>
            
            {/* Days Remaining */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Jours restants</div>
              <div className={`text-2xl font-bold ${
                currentDeadline?.is_passed ? 'text-red-600' :
                currentDeadline?.jours_restants <= 7 ? 'text-amber-600' :
                currentDeadline?.jours_restants <= 30 ? 'text-orange-600' :
                'text-green-600'
              }`}>
                {currentDeadline?.is_passed ? (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-5 w-5" />
                    Expirée
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Clock className="h-5 w-5" />
                    {currentDeadline?.jours_restants} jours
                  </span>
                )}
              </div>
            </div>
            
            {/* Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Statut</div>
              <div className="flex items-center gap-2">
                {currentDeadline?.is_passed ? (
                  <Badge variant="destructive">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Échéance passée
                  </Badge>
                ) : (
                  <Badge variant="success" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {/* Extension reason if applicable */}
          {currentDeadline?.is_extended && currentDeadline?.motif_extension && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="text-sm font-medium text-amber-800 mb-1">Motif de l'extension :</div>
              <div className="text-sm text-amber-700">{currentDeadline.motif_extension}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modify Deadline Form - Only for Super Admin */}
      {isSuperAdmin && (
        <Card data-testid="modify-deadline-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Save className="h-5 w-5" />
              Modifier la Date Limite
            </CardTitle>
            <CardDescription>
              Modifiez la date limite en cas de décision exceptionnelle. 
              Cette action sera enregistrée dans l'historique.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="annee">Année du bilan</Label>
                  <Input
                    id="annee"
                    type="number"
                    value={formData.annee}
                    onChange={(e) => setFormData(prev => ({ ...prev, annee: parseInt(e.target.value) }))}
                    min={new Date().getFullYear() - 5}
                    max={new Date().getFullYear()}
                    data-testid="deadline-year-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_limite">Nouvelle date limite</Label>
                  <Input
                    id="date_limite"
                    type="date"
                    value={formData.date_limite}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_limite: e.target.value }))}
                    min={`${formData.annee + 1}-01-01`}
                    max={`${formData.annee + 1}-09-30`}
                    data-testid="deadline-date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="motif">Motif de la modification (obligatoire)</Label>
                <Textarea
                  id="motif"
                  placeholder="Expliquez la raison de cette modification exceptionnelle..."
                  value={formData.motif}
                  onChange={(e) => setFormData(prev => ({ ...prev, motif: e.target.value }))}
                  rows={3}
                  data-testid="deadline-motif-textarea"
                />
                <p className="text-xs text-gray-500">Minimum 10 caractères</p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button 
                  type="submit" 
                  disabled={saving}
                  data-testid="save-deadline-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
                
                {currentDeadline?.is_extended && (
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={resetting}
                    data-testid="reset-deadline-btn"
                  >
                    {resetting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                        Réinitialisation...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Réinitialiser au 31 mars
                      </>
                    )}
                  </Button>
                )}
                
                <Button 
                  type="button"
                  variant="ghost"
                  onClick={fetchHistory}
                  data-testid="view-history-btn"
                >
                  <History className="h-4 w-4 mr-2" />
                  Voir l'historique
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* History Modal/Section */}
      {showHistory && (
        <Card data-testid="deadline-history-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Historique des modifications
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowHistory(false)}
              >
                Fermer
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {deadlineHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucune modification enregistrée</p>
            ) : (
              <div className="space-y-3">
                {deadlineHistory.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="font-medium">
                        Bilan {item.annee_bilan}
                      </div>
                      <div className="text-sm text-gray-600">
                        Date limite: {item.date_limite_formatted}
                      </div>
                      {item.motif_extension && (
                        <div className="text-sm text-gray-500 mt-1">
                          Motif: {item.motif_extension}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {item.is_extended ? (
                        <Badge variant="warning" className="bg-amber-100 text-amber-800">
                          Prolongée
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          Par défaut
                        </Badge>
                      )}
                      {item.modified_by && (
                        <div className="text-xs text-gray-500 mt-1">
                          Par: {item.modified_by}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card for non-super admins */}
      {!isSuperAdmin && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-blue-800">Accès limité</div>
                <p className="text-sm text-blue-700 mt-1">
                  Seul le Super Administrateur peut modifier la date limite de soumission.
                  Contactez le Super Administrateur si une modification est nécessaire.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Database Reset Section - Super Admin Only */}
      {isSuperAdmin && (
        <Card className="border-red-200" data-testid="database-reset-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Zone Dangereuse - Réinitialisation Base de Données
            </CardTitle>
            <CardDescription>
              Cette action supprimera TOUTES les données (cabinets, entreprises, bilans, etc.) 
              sauf votre compte Super Admin. Cette action est irréversible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showResetConfirm ? (
              <Button 
                variant="destructive"
                onClick={() => setShowResetConfirm(true)}
                data-testid="show-reset-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Réinitialiser la Base de Données
              </Button>
            ) : (
              <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                  <div>
                    <div className="font-bold text-red-800">⚠️ ATTENTION - Action Irréversible</div>
                    <p className="text-sm text-red-700 mt-1">
                      Vous êtes sur le point de supprimer TOUTES les données de la plateforme.
                      Pour confirmer, tapez <strong>CONFIRMER SUPPRESSION</strong> ci-dessous.
                    </p>
                  </div>
                </div>
                
                <Input
                  placeholder="Tapez: CONFIRMER SUPPRESSION"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  className="border-red-300"
                  data-testid="reset-confirm-input"
                />
                
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    disabled={resetConfirmText !== "CONFIRMER SUPPRESSION" || resettingDb}
                    onClick={async () => {
                      try {
                        setResettingDb(true);
                        const response = await fetch(`${API_URL}/api/admins/system/reset-database`, {
                          method: "DELETE",
                          headers: {
                            "Authorization": `Bearer ${token}`
                          }
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          toast.success("Base de données réinitialisée avec succès");
                          setShowResetConfirm(false);
                          setResetConfirmText("");
                          // Refresh page after reset
                          window.location.reload();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || "Erreur lors de la réinitialisation");
                        }
                      } catch (error) {
                        console.error(/* PROD */ "Error resetting database:", error);
                        toast.error("Erreur lors de la réinitialisation");
                      } finally {
                        setResettingDb(false);
                      }
                    }}
                    data-testid="confirm-reset-btn"
                  >
                    {resettingDb ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Suppression en cours...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Confirmer la Suppression
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowResetConfirm(false);
                      setResetConfirmText("");
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Signature Pad Dialog */}
      <SignaturePad
        open={showSignaturePad}
        onClose={() => setShowSignaturePad(false)}
        onSave={handleSaveSignature}
        existingSignature={signatureData}
        title="Signature Électronique Officielle"
        description="Cette signature sera utilisée sur tous les documents officiels de l'IGT"
      />
    </div>
  );
}
